import React from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { 
  Shield, 
  Crown, 
  Anchor, 
  Heart, 
  Camera,
  Building2,
  Users,
  Truck,
  HelpCircle
} from 'lucide-react';
import HelicopterSilhouetteIcon from '@/components/icons/HelicopterSilhouetteIcon';

interface ConfigurationBadgeProps {
  configuration?: string;
  className?: string;
}

// Map configuration names to icons and colors
const configStyles: Record<string, { 
  icon: React.ElementType | null; 
  bgClass: string; 
  textClass: string;
  label?: string;
  useHelicopterIcon?: boolean;
}> = {
  'SAR': { 
    icon: Shield, 
    bgClass: 'bg-red-100 dark:bg-red-900/30',
    textClass: 'text-red-700 dark:text-red-400',
    label: 'Search & Rescue'
  },
  'Search & Rescue': { 
    icon: Shield, 
    bgClass: 'bg-red-100 dark:bg-red-900/30',
    textClass: 'text-red-700 dark:text-red-400'
  },
  'VIP': { 
    icon: Crown, 
    bgClass: 'bg-amber-100 dark:bg-amber-900/30',
    textClass: 'text-amber-700 dark:text-amber-400',
    label: 'VIP Executive'
  },
  'VIP Executive': { 
    icon: Crown, 
    bgClass: 'bg-amber-100 dark:bg-amber-900/30',
    textClass: 'text-amber-700 dark:text-amber-400'
  },
  'Executive': { 
    icon: Crown, 
    bgClass: 'bg-amber-100 dark:bg-amber-900/30',
    textClass: 'text-amber-700 dark:text-amber-400'
  },
  'EMS': { 
    icon: Heart, 
    bgClass: 'bg-pink-100 dark:bg-pink-900/30',
    textClass: 'text-pink-700 dark:text-pink-400',
    label: 'Medical'
  },
  'HEMS': { 
    icon: Heart, 
    bgClass: 'bg-pink-100 dark:bg-pink-900/30',
    textClass: 'text-pink-700 dark:text-pink-400',
    label: 'Medical'
  },
  'Medical': { 
    icon: Heart, 
    bgClass: 'bg-pink-100 dark:bg-pink-900/30',
    textClass: 'text-pink-700 dark:text-pink-400'
  },
  'Offshore': { 
    icon: Anchor, 
    bgClass: 'bg-blue-100 dark:bg-blue-900/30',
    textClass: 'text-blue-700 dark:text-blue-400'
  },
  'Utility': { 
    icon: Truck, 
    bgClass: 'bg-slate-100 dark:bg-slate-800',
    textClass: 'text-slate-700 dark:text-slate-400'
  },
  'Corporate': { 
    icon: Building2, 
    bgClass: 'bg-violet-100 dark:bg-violet-900/30',
    textClass: 'text-violet-700 dark:text-violet-400'
  },
  'Passenger': { 
    icon: Users, 
    bgClass: 'bg-emerald-100 dark:bg-emerald-900/30',
    textClass: 'text-emerald-700 dark:text-emerald-400'
  },
  'Tour': { 
    icon: Camera, 
    bgClass: 'bg-cyan-100 dark:bg-cyan-900/30',
    textClass: 'text-cyan-700 dark:text-cyan-400'
  },
  'Aerial Work': { 
    icon: null, 
    useHelicopterIcon: true,
    bgClass: 'bg-orange-100 dark:bg-orange-900/30',
    textClass: 'text-orange-700 dark:text-orange-400'
  },
};

export const ConfigurationBadge: React.FC<ConfigurationBadgeProps> = ({
  configuration,
  className
}) => {
  if (!configuration) return null;

  // Try to match configuration or use default
  const style = configStyles[configuration] || {
    icon: HelpCircle,
    bgClass: 'bg-muted',
    textClass: 'text-muted-foreground',
    label: undefined,
    useHelicopterIcon: false
  };

  const Icon = style.icon;
  const displayLabel = style.label || configuration;

  return (
    <Badge 
      variant="secondary"
      className={cn(
        "text-[10px] px-2 py-0.5 font-semibold uppercase tracking-wide gap-1",
        style.bgClass,
        style.textClass,
        "border-0",
        className
      )}
    >
      {style.useHelicopterIcon ? (
        <HelicopterSilhouetteIcon className="w-3 h-3" />
      ) : Icon ? (
        <Icon className="w-3 h-3" />
      ) : null}
      {displayLabel}
    </Badge>
  );
};

export default ConfigurationBadge;
